import { Link } from "react-router-dom";
import { Printer, Download, ArrowLeft, Leaf, Recycle, Droplets, Repeat, CheckCircle2, Sprout } from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";

const B = ({ t }) => <>{t.split(/(\*\*[^*]+\*\*)/g).map((s, i) => s.startsWith("**") ? <strong key={i} className="text-ink font-semibold">{s.slice(2, -2)}</strong> : s)}</>;

const PILLARS = [
  { icon: Recycle, title: "Circular Material", rows: ["**100% recyclable** HDPE construction.", "Reused across **many project cycles.**", "**Reclaimed & re-milled** at end of life.", "Diverts mats from **landfill.**"] },
  { icon: Droplets, title: "Soil & Water Protection", rows: ["**Non-porous** — zero fluid absorption.", "**No hydrocarbon leaching** into soils.", "Protects **wetlands & sensitive habitat.**", "**SWPPP-compatible** ground cover."] },
  { icon: Repeat, title: "Lower Lifecycle Footprint", rows: ["**Fewer assets** manufactured & shipped.", "**Zero replacements** = zero re-manufacture.", "Reduced **haul & disposal** trips.", "Longer life → **lower embodied carbon.**"] },
];

const METRICS = [
  { icon: Recycle, v: "100%", l: "recyclable material" },
  { icon: Sprout, v: "0", l: "hydrocarbon leaching" },
  { icon: Leaf, v: "10+ yrs", l: "reuse before end-of-life" },
];

export default function SustainabilityBrief() {
  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      <div className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 print:hidden">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/products/big-bad-mats" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm"><ArrowLeft className="h-4 w-4" /> Back to product</Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-white/50">Sustainability / ESG · One-Page</span>
            <a href="/big-bad-mats-sustainability.pdf" download data-testid="download-sustainability" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:border-flame hover:text-flame transition-colors"><Download className="h-4 w-4" /> Download PDF</a>
            <button onClick={() => window.print()} data-testid="print-sustainability" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors"><Printer className="h-4 w-4" /> Print</button>
          </div>
        </div>
      </div>

      <div className="py-8 print:py-0 flex justify-center">
        <div data-testid="sustainability-sheet" className="bg-white shadow-2xl print:shadow-none w-[297mm] h-[210mm] p-[11mm] flex flex-col" style={{ fontSize: "10px" }}>
          <header className="flex items-start justify-between border-b-2 border-ink pb-3">
            <div className="max-w-[66%]">
              <div className="flex items-center gap-2 mb-1.5">
                <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVEST</span>
                <span className="ml-2 border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.18em]">Sustainability &amp; ESG Statement</span>
              </div>
              <h1 className="font-display font-black tracking-tight text-ink leading-[1] text-[31px]">Protect the Ground. Divert the Waste.</h1>
              <p className="mt-2 text-charcoal text-[11px]">Big Bad Mats support your <span className="font-semibold text-ink">ESG and bid-compliance</span> goals — a durable, recyclable, non-leaching ground interface that protects sensitive ecosystems and shrinks lifecycle footprint.</p>
            </div>
            <div className="w-[32%] bg-ink text-white p-3 grid grid-cols-3 gap-2 text-center">
              {METRICS.map((m) => (
                <div key={m.l}>
                  <m.icon className="h-4 w-4 text-flame mx-auto" />
                  <div className="font-display font-extrabold text-flame text-[16px] leading-none mt-1">{m.v}</div>
                  <div className="text-[7px] uppercase tracking-wider text-white/50 mt-0.5">{m.l}</div>
                </div>
              ))}
            </div>
          </header>

          <div className="flex-1 grid grid-cols-3 gap-4 pt-4 min-h-0">
            {PILLARS.map((p) => (
              <div key={p.title} className="border-2 border-flame flex flex-col">
                <div className="bg-flame text-white px-4 py-2 flex items-center gap-2"><p.icon className="h-4 w-4" /><span className="font-display font-bold text-[12px] uppercase tracking-wider">{p.title}</span></div>
                <ul className="p-4 space-y-2.5 flex-1 flex flex-col justify-center">
                  {p.rows.map((x) => (
                    <li key={x} className="flex items-start gap-2 text-charcoal text-[11px] leading-snug"><CheckCircle2 className="h-3.5 w-3.5 text-flame flex-shrink-0 mt-0.5" /><span><B t={x} /></span></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-4 bg-ink text-white px-6 py-3">
            <div className="flex items-center gap-2"><Leaf className="h-4 w-4 text-flame" /><span className="font-display font-bold text-[13px] uppercase tracking-wider">Why It Matters to Your Bid</span></div>
            <div className="mt-2 grid grid-cols-3 gap-3 text-white/70 text-[10px]">
              <div className="bg-white/5 border border-white/10 px-4 py-2.5"><span className="text-flame font-semibold">Compliance:</span> attach to EPCM &amp; enterprise ESG submittals.</div>
              <div className="bg-white/5 border border-white/10 px-4 py-2.5"><span className="text-flame font-semibold">Liability:</span> non-leach surface reduces remediation exposure.</div>
              <div className="bg-white/5 border border-white/10 px-4 py-2.5"><span className="text-flame font-semibold">Reporting:</span> quantify landfill diversion &amp; reuse cycles.</div>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-4 border-t border-border pt-3">
            <div>
              <div className="font-display font-extrabold text-ink text-[15px]">Need ESG documentation for your submittal?</div>
              <div className="text-muted-foreground text-[10px] mt-0.5">{COMPANY.phone} · {COMPANY.email} · {COMPANY.serviceArea}</div>
            </div>
            <Link to="/request-quote" className="inline-flex items-center bg-flame text-white px-5 py-3 font-semibold text-[12px] hover:bg-flame/90 transition-colors print:hidden flex-shrink-0">Request ESG &amp; Material Docs</Link>
          </div>
          <p className="mt-2 text-[8px] text-muted-foreground text-center">Environmental claims are representative of HDPE composite performance; request certified material &amp; recyclability documentation. © {new Date().getFullYear()} {COMPANY.legal}.</p>
        </div>
      </div>
    </div>
  );
}
